<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/firebase'
import { useAuthStore } from '@/stores/auth'
import { useToastStore } from '@/stores/toast'
import { useSettingsStore } from '@/stores/settings'
import { MEDICAL_QUAL_OPTIONS, SEARCHER_TYPE_OPTIONS, ROPE_QUAL_OPTIONS, STANDARD_MISC_QUALS } from '@/types'
import type { SearcherType, RopeQual, Agency } from '@/types'
import FosInput from '@/components/ui/FosInput.vue'
import FosButton from '@/components/ui/FosButton.vue'

const router = useRouter()
const toast = useToastStore()
const authStore = useAuthStore()
const settingsStore = useSettingsStore()

const profile = authStore.userProfile

const firstName = ref(profile?.user_first_name ?? '')
const lastName = ref(profile?.user_last_name ?? '')
const phone = ref(profile?.phone ?? '')
const medicalQual = ref(profile?.medical_qual ?? '')
const searcherType = ref<SearcherType | null>((profile?.searcher_type as SearcherType) ?? null)
const ropeQual = ref<RopeQual | null>((profile?.rope_qual as RopeQual) ?? null)
const miscQuals = ref<string[]>(profile?.misc_quals ?? [])

const agency = ref<Agency | null>(null)
const saving = ref(false)

onMounted(async () => {
  const agencyId = authStore.userProfile?.agency_id
  if (!agencyId) return
  const snap = await getDoc(doc(db, 'agencies', agencyId))
  if (snap.exists()) {
    agency.value = { agency_id: snap.id, ...snap.data() } as Agency
  }
})

function toggleMiscQual(qual: string) {
  const idx = miscQuals.value.indexOf(qual)
  if (idx === -1) miscQuals.value.push(qual)
  else miscQuals.value.splice(idx, 1)
}

async function handleSave() {
  saving.value = true
  try {
    await authStore.saveUserProfile({
      user_first_name: firstName.value.trim(),
      user_last_name: lastName.value.trim(),
      phone: phone.value.trim(),
      medical_qual: medicalQual.value,
      searcher_type: searcherType.value,
      rope_qual: ropeQual.value,
      misc_quals: miscQuals.value,
    })
    toast.add('Profile updated.', 'success')
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'An error occurred'
    toast.add(msg, 'error')
  } finally {
    saving.value = false
  }
}

const themeOptions = [
  { value: 'dark' as const, label: 'DARK' },
  { value: 'light' as const, label: 'LIGHT' },
  { value: 'system' as const, label: 'SYSTEM' },
]
</script>

<template>
  <div class="h-full flex flex-col">
    <header class="h-12 flex items-center justify-between pr-4 bg-background border-b border-border shrink-0">
      <div class="flex items-center h-full overflow-hidden">
        <div class="w-20 flex items-center justify-center gap-2 shrink-0">
          <div class="w-[24px] shrink-0" />
          <img src="/assets/logo.svg" alt="First on Scene" class="h-8 w-auto shrink-0" />
        </div>
        <div class="h-4 w-px bg-border shrink-0" />
        <div class="flex items-center gap-2.5 pl-2.5 overflow-hidden">
          <span class="font-headline font-bold text-sm text-on-surface uppercase tracking-tight truncate">
            First On Scene
          </span>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <ThemeToggle />
        <UserAvatar />
      </div>
    </header>

    <div class="flex-1 overflow-y-auto flex items-start justify-center px-4 py-8 sm:px-6 sm:py-12">
      <div class="max-w-xl w-full flex flex-col gap-0">

        <div class="bg-surface-container px-6 pt-8 pb-6 sm:px-10 sm:pt-10">
          <h1 class="text-2xl font-bold text-on-surface uppercase tracking-wide">User Settings</h1>
          <p class="text-on-surface-variant mt-1 text-sm">Manage your profile, qualifications, and preferences.</p>
        </div>

        <!-- Profile & Qualifications -->
        <div class="bg-surface-container px-6 pb-6 sm:px-10 sm:pb-8 mt-px">
          <div class="border-t border-outline-variant/15 pt-6">
            <p class="text-[10px] font-label uppercase tracking-widest text-outline mb-6">Profile &amp; Qualifications</p>

            <div class="flex flex-col gap-8">
              <!-- Name -->
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="flex flex-col gap-1.5">
                  <label class="text-[10px] font-label uppercase tracking-widest text-outline">First Name</label>
                  <FosInput v-model="firstName" placeholder="First name" />
                </div>
                <div class="flex flex-col gap-1.5">
                  <label class="text-[10px] font-label uppercase tracking-widest text-outline">Last Name</label>
                  <FosInput v-model="lastName" placeholder="Last name" />
                </div>
              </div>

              <!-- Agency (read-only) -->
              <div class="flex flex-col gap-1.5">
                <label class="text-[10px] font-label uppercase tracking-widest text-outline">Agency</label>
                <div class="flex items-center gap-3 py-2">
                  <img
                    v-if="agency?.agency_logo"
                    :src="agency.agency_logo"
                    :alt="agency.agency_name"
                    class="h-8 w-8 object-contain"
                  />
                  <span class="text-on-surface-variant text-sm">
                    {{ agency?.agency_name ?? '—' }}
                  </span>
                </div>
                <p class="text-xs text-outline">Contact your admin to change agency.</p>
              </div>

              <!-- Phone -->
              <div class="flex flex-col gap-1.5">
                <label class="text-[10px] font-label uppercase tracking-widest text-outline">Phone</label>
                <FosInput v-model="phone" type="tel" placeholder="(555) 000-0000" />
              </div>

              <div class="h-px bg-outline-variant/15" />

              <!-- Medical Qualification -->
              <div class="flex flex-col gap-3">
                <label class="text-[10px] font-label uppercase tracking-widest text-outline">Medical Qualification</label>
                <div class="flex flex-wrap gap-x-6 gap-y-3">
                  <label v-for="opt in MEDICAL_QUAL_OPTIONS" :key="opt" class="flex items-center gap-2 cursor-pointer text-sm text-on-surface-variant">
                    <input type="radio" :value="opt" v-model="medicalQual" class="accent-primary" />
                    {{ opt }}
                  </label>
                </div>
              </div>

              <!-- Searcher Type -->
              <div class="flex flex-col gap-3">
                <label class="text-[10px] font-label uppercase tracking-widest text-outline">Searcher Type</label>
                <div class="flex flex-wrap gap-x-6 gap-y-3">
                  <label v-for="opt in SEARCHER_TYPE_OPTIONS" :key="opt" class="flex items-center gap-2 cursor-pointer text-sm text-on-surface-variant">
                    <input type="radio" :value="opt" v-model="searcherType" class="accent-primary" />
                    {{ opt }}
                  </label>
                </div>
              </div>

              <!-- Rope Qualification -->
              <div class="flex flex-col gap-3">
                <label class="text-[10px] font-label uppercase tracking-widest text-outline">Rope Qualification</label>
                <div class="flex flex-wrap gap-x-6 gap-y-3">
                  <label v-for="opt in ROPE_QUAL_OPTIONS" :key="opt" class="flex items-center gap-2 cursor-pointer text-sm text-on-surface-variant">
                    <input type="radio" :value="opt" v-model="ropeQual" class="accent-primary" />
                    {{ opt }}
                  </label>
                </div>
              </div>

              <div class="h-px bg-outline-variant/15" />

              <!-- Other Qualifications -->
              <div class="flex flex-col gap-3">
                <label class="text-[10px] font-label uppercase tracking-widest text-outline">Other Qualifications</label>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  <label v-for="opt in STANDARD_MISC_QUALS" :key="opt" class="flex items-center gap-2 cursor-pointer text-sm text-on-surface-variant">
                    <input
                      type="checkbox"
                      :value="opt"
                      :checked="miscQuals.includes(opt)"
                      class="accent-primary"
                      @change="toggleMiscQual(opt)"
                    />
                    {{ opt }}
                  </label>
                </div>
              </div>
            </div>

            <FosButton variant="primary" class="w-full mt-8" :disabled="saving" @click="handleSave">
              <span v-if="saving" class="w-4 h-4 border-2 border-outline/30 border-t-outline animate-spin" />
              <span v-else>Save Changes</span>
            </FosButton>
          </div>
        </div>

        <!-- Appearance -->
        <div class="bg-surface-container-low px-6 py-6 sm:px-10 sm:py-8 mt-px">
          <div class="border-t border-outline-variant/15 pt-6">
            <p class="text-[10px] font-label uppercase tracking-widest text-outline mb-6">Appearance</p>

            <div class="flex items-center justify-between gap-4">
              <div class="flex flex-col gap-0.5">
                <span class="text-sm text-on-surface font-medium">Theme</span>
                <span class="text-xs text-outline">
                  <template v-if="settingsStore.theme === 'system'">
                    Following system ({{ settingsStore.resolvedTheme === 'dark' ? 'dark' : 'light' }})
                  </template>
                  <template v-else>
                    Set manually
                  </template>
                </span>
              </div>

              <div class="flex gap-1">
                <FosButton
                  v-for="opt in themeOptions"
                  :key="opt.value"
                  :variant="settingsStore.theme === opt.value ? 'primary' : 'secondary'"
                  size="sm"
                  @click="settingsStore.setTheme(opt.value)"
                >
                  {{ opt.label }}
                </FosButton>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>
